// BR-37 — the one place the access model is said in Spanish.
//
// The backend's catalogue (`mantis/src/services/access/catalogue.js`) owns the codes and which
// role holds which; it deliberately carries no labels, so that the words people read live in one
// file here instead of drifting between two repos. Every screen that names a permission, a role
// or the access mode takes the words from this file — a label written inline somewhere else is
// the drift this exists to prevent, and `cierreAcceso.test.js` fails if a catalogue code has no
// entry here.
//
// The descriptions say what the permission lets a person DO on the Cierres screens, not what the
// code is called: an administrator choosing a role is deciding whether someone may reopen a
// closing, not whether they hold `cierres.closings.reopen`.

export const GRUPOS_DE_PERMISOS = [
  { clave: "consulta", titulo: "Consulta" },
  { clave: "operacion", titulo: "Operación" },
  { clave: "administracion", titulo: "Administración" },
];

// Catalogue order, which is the order the backend lists them in.
export const PERMISOS = {
  "cierres.closings.view": {
    nombre: "Ver cierres",
    descripcion: "El panel, el calendario, las oficinas y el detalle de cada cierre.",
    grupo: "consulta",
    accion: "ver estos cierres",
  },
  "cierres.documents.view": {
    nombre: "Ver documentos",
    descripcion: "Abrir los documentos que se cargaron para un cierre.",
    grupo: "consulta",
    accion: "ver los documentos de este cierre",
  },
  "cierres.reports.view": {
    nombre: "Ver reportes",
    descripcion: "Los reportes e indicadores de cierres.",
    grupo: "consulta",
    accion: "ver los reportes",
  },
  "cierres.export": {
    nombre: "Exportar",
    descripcion: "Descargar el panel y los reportes en Excel.",
    grupo: "consulta",
    accion: "exportar estos datos",
  },
  "cierres.chat.use": {
    nombre: "Usar el asistente",
    descripcion: "Preguntar al asistente por cierres, oficinas y cifras.",
    grupo: "consulta",
    accion: "usar el asistente",
  },
  "cierres.closings.create": {
    nombre: "Crear cierres",
    descripcion: "Abrir el cierre de una oficina para una fecha.",
    grupo: "operacion",
    accion: "abrir cierres en esta oficina",
  },
  "cierres.documents.upload": {
    nombre: "Cargar documentos",
    descripcion: "Subir un documento a un cierre y leerlo.",
    grupo: "operacion",
    accion: "cargar ni leer documentos de este cierre",
  },
  "cierres.datapoints.edit": {
    nombre: "Corregir valores",
    descripcion: "Corregir un valor leído de un documento o ingresar uno que falta.",
    grupo: "operacion",
    accion: "corregir valores de este cierre",
  },
  "cierres.closings.evaluate": {
    nombre: "Evaluar cierres",
    descripcion: "Volver a calcular un cierre con sus datos actuales.",
    grupo: "operacion",
    accion: "evaluar este cierre",
  },
  "cierres.alerts.resolve": {
    nombre: "Atender alertas",
    descripcion: "Dar por atendida una alerta, con una observación.",
    grupo: "operacion",
    accion: "atender alertas de este cierre",
  },
  "cierres.closings.reopen": {
    nombre: "Reabrir cierres",
    descripcion: "Devolver a revisión un cierre ya terminado.",
    grupo: "operacion",
    accion: "reabrir este cierre",
  },
  "cierres.daily.run": {
    nombre: "Procesar el día",
    descripcion: "Cargar los reportes diarios de un cliente completo. Requiere el cliente entero.",
    grupo: "operacion",
    accion: "procesar el día de este cliente",
  },
  "cierres.calendar.manage": {
    nombre: "Administrar sábados laborales",
    descripcion: "Agregar, cambiar o quitar los sábados que trabaja un cliente. Requiere el cliente entero; de los roles incluidos, solo Administrador.",
    grupo: "administracion",
    accion: "cambiar los sábados laborales de este cliente",
  },
  "cierres.offices.contacts": {
    nombre: "Administrar correos de oficinas",
    descripcion: "Cambiar los correos a los que cada oficina recibe su propio cuadre, uno por uno o desde un Excel.",
    grupo: "administracion",
    accion: "cambiar los correos de esta oficina",
  },
  "cierres.offices.configure": {
    nombre: "Configurar oficinas",
    descripcion: "Encender o apagar la máquina GLORY de cada oficina. Apagada, la oficina envía su conteo manual firmado en lugar de la GLORY.",
    grupo: "administracion",
    accion: "cambiar la configuración de esta oficina",
  },
  "cierres.notifications.manage": {
    nombre: "Configurar alertas por correo",
    descripcion: "Cambiar quién recibe los correos de alertas del cliente (el resumen diario y el aviso inmediato) y qué alertas se avisan de inmediato. Requiere el cliente entero; de los roles incluidos, solo Administrador.",
    grupo: "administracion",
    accion: "cambiar la configuración de alertas de este cliente",
  },
  "cierres.team.view": {
    nombre: "Ver el tablero del equipo",
    descripcion: "La pestaña Equipo de Reportes: la cola de trabajo, el trabajo de cada persona, los tiempos de respuesta y el flujo de cada día. Requiere el cliente entero; de los roles incluidos, Administrador y Supervisor.",
    grupo: "consulta",
    accion: "ver el tablero del equipo de este cliente",
  },
  "access.users.manage": {
    nombre: "Administrar usuarios",
    descripcion: "Dar y quitar acceso a otras personas, dentro de su propio alcance.",
    grupo: "administracion",
    accion: "administrar usuarios",
  },
  "access.roles.manage": {
    nombre: "Administrar roles",
    descripcion: "Crear y editar los roles propios del negocio. Requiere el permiso sobre todos los clientes.",
    grupo: "administracion",
    accion: "administrar roles",
  },
};

export const CODIGOS_DE_PERMISO = Object.keys(PERMISOS);

// Keyed by the string an assignment stores. A custom role is named by its Mongo id and carries its
// own name, which a person typed — it never comes through here.
export const ROLES_PREDEFINIDOS = {
  administrador: {
    nombre: "Administrador",
    descripcion: "Todo, incluidos los roles propios del negocio.",
  },
  supervisor: {
    nombre: "Supervisor",
    descripcion: "Todo menos administrar roles. Administra usuarios solo dentro de su alcance.",
  },
  operador: {
    nombre: "Operador",
    descripcion:
      "Trabaja los cierres: consulta, carga documentos, corrige, evalúa, atiende alertas y exporta. No reabre y no administra usuarios.",
  },
  auditor: {
    nombre: "Auditor",
    descripcion: "Consulta y exporta todo lo de su alcance, sin cambiar nada.",
  },
  cliente: {
    nombre: "Cliente",
    descripcion: "Lo mismo que Auditor, sobre las oficinas y procesos de un solo cliente.",
  },
};

export const MODOS_DE_ACCESO = {
  off: {
    nombre: "Apagado",
    aviso:
      "El acceso por cliente y oficina todavía no se aplica: hoy cada usuario ve todos los clientes. Lo que configures aquí empieza a regir cuando Mantis active el modo de prueba y después el obligatorio.",
  },
  shadow: {
    nombre: "De prueba",
    aviso:
      "Modo de prueba: cada persona sigue viendo lo de hoy, y se registra lo que dejaría de ver o de hacer con el acceso que tiene configurado. Revísalo antes de que el acceso pase a ser obligatorio.",
  },
  enforced: {
    nombre: "Obligatorio",
    aviso: "El acceso es obligatorio: cada persona ve y hace solo lo que su rol y su alcance permiten.",
  },
};

export const nombreDePermiso = (codigo) => PERMISOS[codigo]?.nombre || codigo;

export const descripcionDePermiso = (codigo) => PERMISOS[codigo]?.descripcion || "";

// BR-38 — what a screen says when a permission is missing: a 403 from an action, or the reason a
// button is disabled. `accion` completes "No tienes permiso para …", so it is written for that
// sentence rather than as a name.
export const sinPermiso = (codigo) =>
  PERMISOS[codigo]?.accion ? `No tienes permiso para ${PERMISOS[codigo].accion}.` : "No tienes permiso para hacer esto.";

// The daily run writes every office's closing, so a region or an office of the client is not
// enough (BR-36's `wholeClientPermits`), and a person holding it on part of the client is owed why.
// "Nuevo cierre" on a client none of whose offices the person may open a closing in.
export const SIN_OFICINAS_ABRIBLES = "No tienes permiso para abrir cierres en las oficinas de este cliente.";

export const SIN_PROCESAR_DIA = `${sinPermiso(
  "cierres.daily.run"
)} Procesar el día escribe el cierre de todas las oficinas del cliente, así que requiere el permiso sobre el cliente completo.`;

// A role as the screens name it: the dictionary's word for a built-in one, the typed name for a
// custom one, and the bare key only for a role that no longer exists (an assignment the backend
// lists in `unresolvedRoles`).
export const nombreDeRol = (clave, roles = []) => {
  if (ROLES_PREDEFINIDOS[clave]) return ROLES_PREDEFINIDOS[clave].nombre;
  const propio = (roles || []).find((r) => r.id === clave);
  return propio ? propio.name : clave;
};

export const descripcionDeRol = (rol) => {
  if (!rol) return "";
  if (rol.builtIn && ROLES_PREDEFINIDOS[rol.id]) return ROLES_PREDEFINIDOS[rol.id].descripcion;
  return rol.description || "";
};

// The backend's refusals are written in Spanish already, but the ones about permissions name them
// by code ("no tienes cierres.export en él"). Said here in the same words the checklist uses.
export const conNombresDePermiso = (texto) =>
  String(texto || "").replace(/\b(?:cierres|access)\.[a-z]+(?:\.[a-z]+)*\b/g, (codigo) =>
    PERMISOS[codigo] ? `«${PERMISOS[codigo].nombre}»` : codigo
  );
